import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotificationFeed } from '@aurora-pvi/contracts';
import { glassFocusRing } from '@aurora-pvi/design-system/glass';
import { createHttpCollaborationRepository } from '../api/httpCollaborationRepository';
import { useSession } from '../app/providers/SessionProvider';

/**
 * What is waiting for you, in the shell.
 *
 * Nothing in this product told anybody that a decision needed them. An approver
 * discovered work by opening the queue and looking, which made the time a
 * decision waited a function of how often somebody happened to check.
 *
 * The count is the server's, not the browser's. Nothing here marks a
 * notification read by rendering it, opening the panel or scrolling past it —
 * only following one or pressing the control does, which is the only thing that
 * makes "unread" mean "you have not seen this". Two tabs open on the same
 * identity therefore agree, because both are reading the same number rather
 * than each keeping their own.
 *
 * In-app is the only channel. `gov.outbox` exists for delivery beyond the app
 * and is deliberately empty; the panel says so rather than implying an email
 * that was never sent.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const MOMENT = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

export function NotificationBell() {
  const repository = useMemo(
    () => (API_BASE_URL ? createHttpCollaborationRepository(API_BASE_URL) : null),
    [],
  );
  const { currentUser } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const feed = useQuery({
    queryKey: ['notifications', currentUser.id],
    queryFn: () => repository?.getNotifications() ?? Promise.resolve(null),
    enabled: Boolean(repository),
    // Long, and deliberately not a live socket: this is a governance queue
    // measured in hours, and a badge that flickers every few seconds asks the
    // reader to watch it.
    refetchInterval: 120_000,
  });

  const markRead = useMutation({
    mutationFn: async (notificationId: string | null) => {
      if (!repository) return null;
      return notificationId === null
        ? repository.markAllNotificationsRead()
        : repository.markNotificationRead(notificationId);
    },
    onSuccess: (next) => {
      if (next) queryClient.setQueryData(['notifications', currentUser.id], next);
      void queryClient.invalidateQueries({ queryKey: ['watchlist'] });
    },
  });

  // Closing on an outside click rather than on blur: blur fires before the
  // click inside the panel lands, which would swallow the first press.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!repository) return null;

  const data = feed.data as NotificationFeed | null | undefined;
  const unread = data?.unreadCount ?? 0;
  const rows = (data?.rows ?? []).slice(0, 6);

  const follow = (id: string, route: string | null) => {
    markRead.mutate(id);
    setOpen(false);
    if (route) navigate(route);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={
          unread === 0
            ? 'Notifications. Nothing unread.'
            : `Notifications. ${unread} unread.`
        }
        className={`relative flex h-9 w-9 items-center justify-center rounded-[10px] border border-[var(--g-edge)] text-[var(--g-text-secondary)] transition-colors hover:bg-[var(--g-surface-2)] ${glassFocusRing}`}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M12 3a6 6 0 0 0-6 6v3.6L4.5 16h15L18 12.6V9a6 6 0 0 0-6-6Z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path d="M10 19a2 2 0 0 0 4 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
        {unread > 0 ? (
          <span
            className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold tabular-nums"
            /* Canvas, not white: the critical token is a light rose against the
               dark theme's near-black and a deep crimson against white, so the
               canvas colour is the one that contrasts with it in both. */
            style={{ background: 'var(--g-critical)', color: 'var(--g-canvas)' }}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Notifications"
          className="absolute right-0 top-11 z-50 flex w-[min(360px,calc(100vw-2rem))] flex-col gap-2 rounded-[var(--g-radius)] border border-[var(--g-edge)] bg-[var(--g-surface-1)] p-3 shadow-[var(--g-shadow)] backdrop-blur-xl"
        >
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[12.5px] font-medium text-[var(--g-text)]">
              {unread === 0 ? 'Nothing unread' : `${unread} unread`}
            </span>
            {unread > 0 ? (
              <button
                type="button"
                onClick={() => markRead.mutate(null)}
                className="text-[11.5px] text-[var(--g-accent)] hover:underline"
              >
                Mark all read
              </button>
            ) : null}
          </div>

          {rows.length === 0 ? (
            <p className="text-[12px] leading-relaxed text-[var(--g-text-muted)]">
              Nothing has been addressed to you. A decision arriving in your approval queue, a
              ruling on one you raised, or somebody drawing you into a discussion will appear here.
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {rows.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => follow(row.id, row.route)}
                    className={`flex w-full flex-col gap-0.5 rounded-[10px] px-2 py-1.5 text-left hover:bg-[var(--g-surface-2)] ${glassFocusRing}`}
                  >
                    <span className="flex items-baseline gap-1.5">
                      {row.isRead ? null : (
                        <span
                          aria-hidden="true"
                          className="h-1.5 w-1.5 shrink-0 rounded-full"
                          style={{
                            background:
                              row.severity === 'Error'
                                ? 'var(--g-critical)'
                                : row.severity === 'Warning'
                                  ? 'var(--g-caution)'
                                  : 'var(--g-accent)',
                          }}
                        />
                      )}
                      <span className="text-[12px] font-medium text-[var(--g-text)]">
                        {row.headline}
                      </span>
                    </span>
                    <span className="text-[11px] text-[var(--g-text-muted)]">
                      {MOMENT.format(new Date(row.receivedAt))} · {row.ruleLabel}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate('/v2/watchlist?tab=notifications');
            }}
            className="self-start text-[11.5px] text-[var(--g-accent)] hover:underline"
          >
            Open the full list
          </button>
        </div>
      ) : null}
    </div>
  );
}
